import type { QueryClient } from '@tanstack/react-query';
import {
  ASSISTANCE_PROGRESS_EVENT,
  type AssistanceProgressNotice,
} from '@evogard/contract';
import { registerRealtimeHandler } from '../../lib/realtime-bus.js';
import { ASSISTANCE_KEY, DISPATCHES_KEY, MANAGEMENT_KEY } from './assistance.js';

/**
 * No máximo uma releitura da tabela a cada `WINDOW_MS`: com vários prestadores
 * em rota, cada um manda a posição a cada 30 s, e a tabela não precisa reler
 * a cada um. A primeira relê na hora; as que chegam dentro da janela viram uma
 * no fim dela.
 */
const WINDOW_MS = 5_000;
const pending = new Map<string, ReturnType<typeof setTimeout>>();

function throttled(key: string, run: () => void): void {
  if (pending.has(key)) return;
  run();
  pending.set(
    key,
    setTimeout(() => {
      pending.delete(key);
      run();
    }, WINDOW_MS),
  );
}

/**
 * O andamento de um acionamento mudou (`assistencia-30`): o prestador marcou
 * um passo, mandou posição, ou a central destravou a placa. A tabela, a
 * Gestão e a mesa aberta daquele acionamento releem — o aviso não carrega o andamento.
 */
registerRealtimeHandler(ASSISTANCE_PROGRESS_EVENT, (payload, client: QueryClient) => {
  const notice = payload as AssistanceProgressNotice;
  throttled('lista', () => void client.invalidateQueries({ queryKey: DISPATCHES_KEY }));
  /* A Gestão (`assistencia-35`) conta a mesma coisa, e relê junto. */
  throttled('gestao', () => void client.invalidateQueries({ queryKey: MANAGEMENT_KEY }));
  throttled(
    `mesa:${notice.dispatchId}`,
    () =>
      void client.invalidateQueries({
        queryKey: [...ASSISTANCE_KEY, 'acionamento', notice.dispatchId],
      }),
  );
});
